/**
 * EmergencyContactEditFields — shared form-fields component for editing
 * an emergency-contact row. Composed by two hosts:
 *
 *   1. `EmergencyContactRowComponent` (in `emergency-contact-row.tsx`) —
 *      patient-chart inline edit-in-list. Done/Cancel/Remove action row,
 *      save deferred to tab-level "Save changes".
 *
 *   2. `EditCallBackContactView` (in `edit-callback-contact-view.tsx`) —
 *      appointment-context modal sub-view. Back/Save header, save commits
 *      via `useSavePatientEmergencyContactList` immediately.
 *
 * Mirrors Flutter `create_emeregency_contact_widget.dart` [sic — Flutter typo].
 *
 * Save-enable rule (`isEmergencyContactSaveEnabled` below): firstName,
 * lastName, contactNumber all non-blank AND `relation.id` set.
 */

import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { CountryDialPicker } from '@/shared/ui/components';
import { useRelations } from '@/shared/services/lookups';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { FieldLabel, FIELD_INPUT_CLASS } from '@/patients/components/personal-details/form-primitives';
import {
  formatPredictionAddress,
  deriveCityCountryFromTerms,
  useAddressSuggestions,
  type AddressPrediction,
} from '@/shared/services/use-address-suggestions';
import type { EmergencyContactRow as Row } from './emergency-contact-schema';

export interface EmergencyContactEditFieldsProps {
  row: Row;
  readOnly?: boolean;
  onChange: (next: Row) => void;
}

export function EmergencyContactEditFields({
  row,
  readOnly,
  onChange,
}: EmergencyContactEditFieldsProps) {
  const [relQuery, setRelQuery] = useState(row.relation.name ?? '');
  useEffect(() => {
    setRelQuery(row.relation.name ?? '');
  }, [row.relation.name]);

  const debouncedRel = useDebounce(relQuery, 250);
  const { data: relations } = useRelations(debouncedRel);
  const [relOpen, setRelOpen] = useState(false);

  // Address autocomplete (F-PAT-242). Mirrors the residential
  // `address-edit-fields` pattern. The Flutter EC widget uses a plain
  // LocationPicker (no manual-entry toggle), so we only render the search
  // picker + Apartment.
  const [addrSearch, setAddrSearch] = useState(row.address ?? '');
  useEffect(() => {
    setAddrSearch(row.address ?? '');
  }, [row.address]);
  const debouncedAddr = useDebounce(addrSearch, 300);
  const { data: addrSuggestions } = useAddressSuggestions({
    input: debouncedAddr,
    enabled: !readOnly,
  });
  const [addrOpen, setAddrOpen] = useState(false);

  const update = (patch: Partial<Row>) => onChange({ ...row, ...patch });

  // On pick: set address (main text) + derive city (terms[n-2]) and country
  // (terms[n-1]). Flutter `create_emeregency_contact_widget.dart:399-417`.
  // lat/lng stay untouched — `/api/Master/GetAddress` doesn't return coords
  // on autocomplete (same documented limitation as residential address).
  const handlePickAddress = (pred: AddressPrediction) => {
    const { mainText, city, country } = deriveCityCountryFromTerms(pred);
    update({
      address: mainText || null,
      city: city || null,
      country: country || null,
    });
    setAddrSearch(mainText);
    setAddrOpen(false);
  };

  // Keep `relationship` (string) and `relation` (object) in sync on the
  // wire — Flutter ships both and the backend parses either.
  useEffect(() => {
    if (row.relation.name !== row.relationship) {
      onChange({ ...row, relationship: row.relation.name ?? null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.relation.name]);

  return (
    <div className="grid gap-6">
      {/* Relationship typeahead — Flutter renders this as the FIRST field
          (`create_emeregency_contact_widget.dart:185`, after the photo). */}
      <div>
        <FieldLabel required>Relationship</FieldLabel>
        <div className="relative mt-1">
          <Input
            value={relQuery}
            onChange={(e) => {
              setRelQuery(e.target.value);
              setRelOpen(true);
            }}
            onFocus={() => setRelOpen(true)}
            onBlur={() => setTimeout(() => setRelOpen(false), 150)}
            placeholder="Enter relationship"
            disabled={readOnly}
            className={FIELD_INPUT_CLASS}
          />
          {relOpen && (relations?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-44 overflow-auto scrollbar-hide rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] shadow-overlay">
              {(relations ?? []).map((r) => (
                <li key={r.id ?? r.name ?? ''}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      update({
                        relation: { id: r.id ?? null, name: r.name ?? null },
                        relationship: r.name ?? null,
                      });
                      setRelQuery(r.name ?? '');
                      setRelOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-grey-100"
                  >
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* First name */}
      <div>
        <FieldLabel required>First name</FieldLabel>
        <Input
          value={row.firstName}
          onChange={(e) => update({ firstName: e.target.value })}
          placeholder="Enter first name"
          maxLength={50}
          className={cn('mt-1', FIELD_INPUT_CLASS)}
          disabled={readOnly}
        />
      </div>

      {/* Middle name */}
      <div>
        <FieldLabel>Middle name</FieldLabel>
        <Input
          value={row.middleName ?? ''}
          onChange={(e) => update({ middleName: e.target.value || null })}
          placeholder="Enter middle name"
          maxLength={50}
          className={cn('mt-1', FIELD_INPUT_CLASS)}
          disabled={readOnly}
        />
      </div>

      {/* Last name */}
      <div>
        <FieldLabel required>Last name</FieldLabel>
        <Input
          value={row.lastName}
          onChange={(e) => update({ lastName: e.target.value })}
          placeholder="Enter last name"
          maxLength={50}
          className={cn('mt-1', FIELD_INPUT_CLASS)}
          disabled={readOnly}
        />
      </div>

      {/* Contact number */}
      <div>
        <FieldLabel required>Contact number</FieldLabel>
        <div className="mt-1">
          <CountryDialPicker
            value={row.contactNumber}
            onChange={(next) => update({ contactNumber: next })}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Address — Google Places autocomplete (F-PAT-242). Flutter
          `create_emeregency_contact_widget.dart:379-426` (LocationPicker). */}
      <div>
        <FieldLabel>Address</FieldLabel>
        <div className="relative mt-1">
          <Input
            value={addrSearch}
            onChange={(e) => {
              setAddrSearch(e.target.value);
              setAddrOpen(true);
              // Keep the free-typed value on the row so a partial entry
              // isn't lost if the user doesn't pick a suggestion.
              update({ address: e.target.value || null });
            }}
            onFocus={() => setAddrOpen(true)}
            onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
            placeholder="Enter address"
            disabled={readOnly}
            className={cn(FIELD_INPUT_CLASS, 'pr-10')}
          />
          <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-primary pointer-events-none" />
          {addrOpen && (addrSuggestions?.predictions?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-56 overflow-auto scrollbar-hide rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] shadow-overlay">
              {(addrSuggestions?.predictions ?? []).map((p) => (
                <li key={p.place_id ?? p.reference}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handlePickAddress(p);
                    }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-grey-100"
                  >
                    {formatPredictionAddress(p)}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* Apartment — Flutter `:434-454` (TextField, maxLength 20). */}
      <div>
        <FieldLabel>Apartment</FieldLabel>
        <Input
          value={row.apartment ?? ''}
          onChange={(e) => update({ apartment: e.target.value || null })}
          placeholder="Enter apartment number"
          maxLength={20}
          className={cn('mt-1', FIELD_INPUT_CLASS)}
          disabled={readOnly}
        />
      </div>

      {/* Primary toggle */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-sm text-text-primary">Primary emergency contact</p>
        <Switch
          checked={row.isProxy}
          onCheckedChange={(checked) => update({ isProxy: checked })}
          disabled={readOnly}
          aria-label="Primary emergency contact"
        />
      </div>
    </div>
  );
}

/**
 * Save-enable rule mirrors `EmergencyContactRowComponent`'s `canDone`
 * check: firstName + lastName + contactNumber non-blank AND a relation
 * is picked (id present).
 */
export function isEmergencyContactSaveEnabled(row: Row): boolean {
  return (
    row.firstName.trim().length > 0 &&
    row.lastName.trim().length > 0 &&
    row.contactNumber.trim().length > 0 &&
    !!row.relation.id
  );
}
